import { Crop } from "@/types/crop";
import { EntryCollection } from "@/types/entryCollection";
import { EntryConfigUnion } from "@/types/entryConfig";
import { CreateExperimentInput, Experiment, UpdateExperimentInput } from "@/types/experiment";
import { Location } from "@/types/location";
import { Plot } from "@/types/plot";
import { Task } from "@/types/task";
import { Trial } from "@/types/trial";
import { UploadCSVFileOptions, UploadResult } from "./storageService";
import * as firebaseService from "./firebase";
import type { OrgScopedOptions } from "./firebase";

export interface DataService {
  // Trial operations
  createTrial: (trialData: Partial<Trial>) => Promise<Trial | null>;
  updateTrial: (trialId: string, trialData: Partial<Trial>) => Promise<boolean>;
  deleteTrial: (trialId: string) => Promise<boolean>;

  // Experiment operations
  createExperiment: (trialId: string, experimentData: CreateExperimentInput) => Promise<Experiment | null>;
  createExperimentWithPlots: (
    trialId: string,
    experimentData: CreateExperimentInput,
    csvFile?: File,
    onProgress?: (progress: number, message: string) => void,
    options?: OrgScopedOptions
  ) => Promise<Experiment | null>;
  duplicateExperimentWithPlots: (
    trialId: string,
    sourceExperiment: Experiment,
    sourcePlots: Plot[],
    overrides: { name: string; description: string; location: string },
    onProgress?: (progress: number, message: string) => void,
    options?: OrgScopedOptions
  ) => Promise<Experiment | null>;
  updateExperiment: (trialId: string, experimentId: string, experimentData: UpdateExperimentInput) => Promise<boolean>;
  deleteExperiment: (trialId: string, experimentId: string, options?: OrgScopedOptions) => Promise<boolean>;

  // Task operations (under experiments - experimentId is required)
  createTask: (trialId: string, experimentId: string, taskData: Omit<Task, "id">) => Promise<Task | null>;
  updateTask: (trialId: string, experimentId: string, taskId: string, updates: Partial<Task>) => Promise<boolean>;
  deleteTask: (trialId: string, experimentId: string, taskId: string) => Promise<boolean>;

  // Plot operations
  createPlot: (trialId: string, experimentId: string, plotData: Partial<Plot>) => Promise<Plot | null>;
  updatePlot: (
    trialId: string,
    experimentId: string,
    plotId: string,
    plotData: Partial<Plot>,
    options?: OrgScopedOptions
  ) => Promise<boolean>;
  deletePlot: (trialId: string, experimentId: string, plotId: string, options?: OrgScopedOptions) => Promise<boolean>;
  createPlotsFromCSV: (
    trialId: string,
    experimentId: string,
    csvFile: File,
    plotMapConfig: {
      plotIDName: string;
      rowIDName?: string;
      colIDName?: string;
      headers: string[];
    },
    onProgress?: (progress: number, message: string) => void,
    options?: OrgScopedOptions
  ) => Promise<{ success: boolean; createdCount: number; errors?: string[] }>;
  createPlotsFromGrid: (
    trialId: string,
    experimentId: string,
    rows: number,
    cols: number,
    onProgress?: (progress: number, message: string) => void,
    options?: OrgScopedOptions
  ) => Promise<{ success: boolean; createdCount: number; errors?: string[] }>;
  generatePlotMapCSV: (rows: number, cols: number) => string;

  // EntryCollection operations
  createEntryCollection: (
    trialId: string,
    experimentId: string,
    taskId: string,
    entryCollectionData: Partial<EntryCollection>
  ) => Promise<EntryCollection | null>;
  updateEntryCollection: (
    trialId: string,
    experimentId: string,
    taskId: string,
    entryCollectionId: string,
    entryCollectionData: Partial<EntryCollection>
  ) => Promise<boolean>;
  deleteEntryCollection: (
    trialId: string,
    experimentId: string,
    taskId: string,
    entryCollectionId: string
  ) => Promise<boolean>;

  // EntryConfig operations
  createEntryConfig: (entryConfigData: Partial<EntryConfigUnion>) => Promise<EntryConfigUnion | null>;
  updateEntryConfig: (
    entryConfigId: string,
    entryConfigData: Partial<EntryConfigUnion>,
    options?: OrgScopedOptions
  ) => Promise<boolean>;
  deleteEntryConfig: (entryConfigId: string, options?: OrgScopedOptions) => Promise<boolean>;
  checkEntryConfigUsage: (
    entryConfigId: string,
    options?: OrgScopedOptions
  ) => Promise<{ isUsed: boolean; trialNames?: string[] }>;

  // Crop operations
  createCrop: (cropData: Partial<Crop>) => Promise<Crop | null>;
  updateCrop: (cropId: string, cropData: Partial<Crop>) => Promise<boolean>;
  deleteCrop: (cropId: string) => Promise<boolean>;

  // Location operations
  createLocation: (locationData: Partial<Location>) => Promise<Location | null>;
  updateLocation: (locationId: string, locationData: Partial<Location>) => Promise<boolean>;
  deleteLocation: (locationId: string) => Promise<boolean>;

  // File upload operations
  uploadCSVFile: (file: File, trialId: string, experimentId: string, options?: UploadCSVFileOptions) => Promise<UploadResult>;
}

export function useDataService(): DataService {
  return firebaseService;
}

// Export individual functions that use Firebase service directly
export function useCreateTrial() {
  return firebaseService.createTrial;
}

export function useUpdateTrial() {
  return firebaseService.updateTrial;
}

export function useDeleteTrial() {
  return firebaseService.deleteTrial;
}

export function useCreateExperiment() {
  return firebaseService.createExperiment;
}

export function useUpdateExperiment() {
  return firebaseService.updateExperiment;
}

export function useDeleteExperiment() {
  return firebaseService.deleteExperiment;
}

export function useCheckExperimentHasTasks() {
  return firebaseService.checkExperimentHasTasks;
}

export function useCreateExperimentWithPlots() {
  return firebaseService.createExperimentWithPlots;
}

export function useDuplicateExperimentWithPlots() {
  return firebaseService.duplicateExperimentWithPlots;
}

export function useCreateTask() {
  return firebaseService.createTask;
}

export function useUpdateTask() {
  return firebaseService.updateTask;
}

export function useDeleteTask() {
  return firebaseService.deleteTask;
}

export function useCreatePlot() {
  return firebaseService.createPlot;
}

export function useUpdatePlot() {
  return firebaseService.updatePlot;
}

export function useDeletePlot() {
  return firebaseService.deletePlot;
}

export function useCreatePlotsFromCSV() {
  return firebaseService.createPlotsFromCSV;
}

export function useCreatePlotsFromGrid() {
  return firebaseService.createPlotsFromGrid;
}

export function useCreatePlotsFromSequence() {
  return firebaseService.createPlotsFromSequence;
}

export function useGeneratePlotMapCSV() {
  return firebaseService.generatePlotMapCSV;
}

export function useValidatePlotMetadataUpdate() {
  return firebaseService.validatePlotMetadataUpdate;
}

export function useUpdatePlotMetadataFromCSV() {
  return firebaseService.updatePlotMetadataFromCSV;
}

export function useCreateEntryCollection() {
  return firebaseService.createEntryCollection;
}

export function useUpdateEntryCollection() {
  return firebaseService.updateEntryCollection;
}

export function useDeleteEntryCollection() {
  return firebaseService.deleteEntryCollection;
}

export function useCreateEntryConfig() {
  return firebaseService.createEntryConfig;
}

export function useUpdateEntryConfig() {
  return firebaseService.updateEntryConfig;
}

export function useDeleteEntryConfig() {
  return firebaseService.deleteEntryConfig;
}

export function useCheckEntryConfigUsage() {
  return firebaseService.checkEntryConfigUsage;
}

export function useArchiveEntryConfig() {
  return firebaseService.archiveEntryConfig;
}

export function useRestoreEntryConfig() {
  return firebaseService.restoreEntryConfig;
}

export function useUploadCSVFile() {
  return firebaseService.uploadCSVFile;
}

// Crop operations
export function useCreateCrop() {
  return firebaseService.createCrop;
}

export function useUpdateCrop() {
  return firebaseService.updateCrop;
}

export function useDeleteCrop() {
  return firebaseService.deleteCrop;
}

// Location operations
export function useCreateLocation() {
  return firebaseService.createLocation;
}

export function useUpdateLocation() {
  return firebaseService.updateLocation;
}

export function useDeleteLocation() {
  return firebaseService.deleteLocation;
}
